import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';

const UserSelector = ({ onUserSelect, selectedUser = null }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [error, setError] = useState(null);

  // Load users
  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const usersRef = collection(db, 'users');
      const q = query(usersRef, orderBy('createdAt', 'desc'), limit(100));
      const querySnapshot = await getDocs(q);
      
      const usersList = [];
      querySnapshot.forEach((doc) => {
        const userData = doc.data();
        usersList.push({
          id: doc.id,
          uid: doc.id,
          email: userData.email || '',
          displayName: userData.displayName || userData.nickname || '',
          nickname: userData.nickname || '',
          role: userData.role || 'customer',
          coins: userData.coins || 0,
          ...userData
        });
      });
      
      setUsers(usersList);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('เกิดข้อผิดพลาดในการโหลดข้อมูลผู้ใช้');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      user.email.toLowerCase().includes(searchLower) ||
      user.displayName.toLowerCase().includes(searchLower) ||
      user.nickname.toLowerCase().includes(searchLower) ||
      user.uid.toLowerCase().includes(searchLower)
    );
  });

  const handleUserClick = (user) => {
    onUserSelect(user);
    setShowDropdown(false);
    setSearchTerm('');
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setShowDropdown(true);
  };

  const handleInputFocus = () => {
    setShowDropdown(true);
  };

  const handleInputBlur = () => {
    // Delay hiding dropdown to allow click events
    setTimeout(() => setShowDropdown(false), 200);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'customer': return 'bg-blue-100 text-blue-800';
      case 'girl': return 'bg-pink-100 text-pink-800';
      case 'momo': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return '👑';
      case 'customer': return '👤';
      case 'girl': return '👩';
      case 'momo': return '👨‍💼';
      default: return '❓';
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
        <p className="text-gray-500 text-sm">กำลังโหลดข้อมูลผู้ใช้...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <span className="text-red-500 text-xl mr-3">❌</span>
            <div>
              <h3 className="text-red-800 font-semibold">เกิดข้อผิดพลาด</h3>
              <p className="text-red-600 text-sm mt-1">{error}</p>
            </div>
          </div>
          <button
            onClick={loadUsers}
            className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
          >
            ลองใหม่
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          เลือกผู้ใช้
        </label>
        
        {/* Selected User Display */}
        {selectedUser ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">{getRoleIcon(selectedUser.role)}</span>
                  <span className="font-semibold text-green-900">
                    {selectedUser.displayName || selectedUser.nickname || 'ไม่มีชื่อ'}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(selectedUser.role)}`}>
                    {selectedUser.role}
                  </span>
                </div>
                <p className="text-green-700 text-sm">{selectedUser.email}</p>
                <p className="text-green-600 text-xs">
                  UID: {selectedUser.uid.substring(0, 20)}...
                </p>
                <p className="text-green-600 text-xs">
                  เหรียญปัจจุบัน: {selectedUser.coins} เหรียญ
                </p>
              </div>
              <button
                onClick={() => onUserSelect(null)}
                className="text-red-500 hover:text-red-700 text-sm"
              >
                🗑️
              </button>
            </div>
          </div>
        ) : (
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              placeholder="ค้นหาด้วย email, ชื่อ, หรือ UID..."
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            
            {/* Dropdown */}
            {showDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {filteredUsers.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    ไม่พบผู้ใช้ที่ตรงกับคำค้นหา
                  </div>
                ) : (
                  filteredUsers.map((user) => (
                    <div
                      key={user.uid}
                      onClick={() => handleUserClick(user)}
                      className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-lg">{getRoleIcon(user.role)}</span>
                            <span className="font-medium text-gray-900">
                              {user.displayName || user.nickname || 'ไม่มีชื่อ'}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                              {user.role}
                            </span>
                          </div>
                          <p className="text-gray-600 text-sm">{user.email}</p>
                          <p className="text-gray-500 text-xs">
                            UID: {user.uid.substring(0, 20)}...
                          </p>
                          <p className="text-gray-500 text-xs">
                            เหรียญ: {user.coins} เหรียญ
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* User Count */}
      <div className="text-sm text-gray-500">
        พบผู้ใช้ทั้งหมด {users.length} คน
      </div>
    </div>
  );
};

export default UserSelector;









